import React from "react";
import * as RSelect from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  /** Currently selected value ("" = placeholder / unselected). */
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Shown when value is "". Also used as the first "clear" option. */
  placeholder: string;
  className?: string;
  ariaLabel?: string;disabled?: boolean;
}

/**
 * Elegant, animated dropdown built on Radix Select.
 * Drop-in replacement for a native <select> with a placeholder option.
 */
const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  className = "",
  ariaLabel,
  disabled = false,
}) => {
  // Radix Select cannot use "" as an item value, so map "" <-> "__all__".
  const ALL = "__all__";
  const items: SelectOption[] = [{ value: ALL, label: placeholder }, ...options];
  const active = value !== "";

  return (
    <RSelect.Root
      value={value === "" ? ALL : value}
      onValueChange={(v) => onChange(v === ALL ? "" : v)}
      disabled={disabled}
    >
      <RSelect.Trigger
        aria-label={ariaLabel || placeholder}
        disabled={disabled}
        className={`rs-trigger ${active ? "is-active" : ""} ${className} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <RSelect.Value placeholder={placeholder} />
        <RSelect.Icon asChild>
          <ChevronDown className="rs-trigger-chevron" />
        </RSelect.Icon>
      </RSelect.Trigger>

      <RSelect.Portal>
        <RSelect.Content
          position="popper"
          sideOffset={6}
          className="rs-content"
        >
          <RSelect.Viewport className="max-h-72">
            {items.map((opt) => (
              <RSelect.Item key={opt.value} value={opt.value} className="rs-item">
                <RSelect.ItemText>{opt.label}</RSelect.ItemText>
                <RSelect.ItemIndicator>
                  <Check className="h-4 w-4 text-merced-blue-700" />
                </RSelect.ItemIndicator>
              </RSelect.Item>
            ))}
          </RSelect.Viewport>
        </RSelect.Content>
      </RSelect.Portal>
    </RSelect.Root>
  );
};

export default Select;
